import {
  RecordingPartRecord,
  RecordingSessionRecord,
  RecordingSessionStatus,
  SegmentPartStatus,
} from "../types/recording";

const DB_NAME = "VoiceRecordingChunkedDB";
const DB_VERSION = 1;
const SESSIONS_STORE = "sessions";
const PARTS_STORE = "parts";

class RecordingSessionService {
  private async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(SESSIONS_STORE)) {
          const sessionStore = db.createObjectStore(SESSIONS_STORE, {
            keyPath: "id",
          });
          sessionStore.createIndex("status", "status", { unique: false });
          sessionStore.createIndex("clinicId", "clinicId", { unique: false });
          sessionStore.createIndex("patientId", "patientId", { unique: false });
        }

        if (!db.objectStoreNames.contains(PARTS_STORE)) {
          const partsStore = db.createObjectStore(PARTS_STORE, {
            keyPath: "id",
          });
          partsStore.createIndex("sessionId", "sessionId", { unique: false });
          partsStore.createIndex("status", "status", { unique: false });
          partsStore.createIndex("sessionPart", ["sessionId", "partNumber"], {
            unique: true,
          });
        }
      };
    });
  }

  async createSession(
    session: Omit<RecordingSessionRecord, "id" | "createdAt" | "updatedAt">,
  ): Promise<RecordingSessionRecord> {
    const now = new Date().toISOString();
    const record: RecordingSessionRecord = {
      ...session,
      id: `chunked_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
      createdAt: now,
      updatedAt: now,
    };

    const db = await this.openDB();
    const tx = db.transaction([SESSIONS_STORE], "readwrite");
    const store = tx.objectStore(SESSIONS_STORE);

    await new Promise<void>((resolve, reject) => {
      const request = store.add(record);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    return record;
  }

  async getSession(sessionId: string): Promise<RecordingSessionRecord | null> {
    const db = await this.openDB();
    const tx = db.transaction([SESSIONS_STORE], "readonly");
    const store = tx.objectStore(SESSIONS_STORE);

    return new Promise((resolve, reject) => {
      const request = store.get(sessionId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async updateSession(
    sessionId: string,
    updates: Partial<RecordingSessionRecord>,
  ): Promise<void> {
    const session = await this.getSession(sessionId);
    if (!session) return;

    const updated: RecordingSessionRecord = {
      ...session,
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    const db = await this.openDB();
    const tx = db.transaction([SESSIONS_STORE], "readwrite");
    const store = tx.objectStore(SESSIONS_STORE);

    await new Promise<void>((resolve, reject) => {
      const request = store.put(updated);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async listSessionsByStatus(
    statuses: RecordingSessionStatus[],
    clinicId?: string,
  ): Promise<RecordingSessionRecord[]> {
    const db = await this.openDB();
    const tx = db.transaction([SESSIONS_STORE], "readonly");
    const store = tx.objectStore(SESSIONS_STORE);

    const sessions = await new Promise<RecordingSessionRecord[]>(
      (resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      },
    );

    return sessions.filter(
      (session) =>
        statuses.includes(session.status) &&
        (!clinicId || session.clinicId === clinicId),
    );
  }

  async storePart(
    sessionId: string,
    partNumber: number,
    blob: Blob,
  ): Promise<RecordingPartRecord> {
    const now = new Date().toISOString();
    const record: RecordingPartRecord = {
      id: `${sessionId}_${partNumber}`,
      sessionId,
      partNumber,
      blob,
      status: "stored",
      createdAt: now,
      updatedAt: now,
      sizeBytes: blob.size,
      uploadAttempts: 0,
    };

    const db = await this.openDB();
    const tx = db.transaction([PARTS_STORE], "readwrite");
    const store = tx.objectStore(PARTS_STORE);

    await new Promise<void>((resolve, reject) => {
      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    return record;
  }

  async getPart(
    sessionId: string,
    partNumber: number,
  ): Promise<RecordingPartRecord | null> {
    const db = await this.openDB();
    const tx = db.transaction([PARTS_STORE], "readonly");
    const index = tx.objectStore(PARTS_STORE).index("sessionPart");

    return new Promise((resolve, reject) => {
      const request = index.get([sessionId, partNumber]);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async listPartsForSession(sessionId: string): Promise<RecordingPartRecord[]> {
    const db = await this.openDB();
    const tx = db.transaction([PARTS_STORE], "readonly");
    const index = tx.objectStore(PARTS_STORE).index("sessionId");

    const parts = await new Promise<RecordingPartRecord[]>(
      (resolve, reject) => {
        const request = index.getAll(sessionId);
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      },
    );

    return parts.sort((a, b) => a.partNumber - b.partNumber);
  }

  async listPartsByStatus(
    sessionId: string,
    statuses: SegmentPartStatus[],
  ): Promise<RecordingPartRecord[]> {
    const allParts = await this.listPartsForSession(sessionId);
    return allParts.filter((part) => statuses.includes(part.status));
  }

  async updatePart(
    sessionId: string,
    partNumber: number,
    updates: Partial<RecordingPartRecord>,
  ): Promise<void> {
    const part = await this.getPart(sessionId, partNumber);
    if (!part) return;

    const updated: RecordingPartRecord = {
      ...part,
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    const db = await this.openDB();
    const tx = db.transaction([PARTS_STORE], "readwrite");
    const store = tx.objectStore(PARTS_STORE);

    await new Promise<void>((resolve, reject) => {
      const request = store.put(updated);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async deleteSessionAndParts(sessionId: string): Promise<void> {
    const db = await this.openDB();
    const tx = db.transaction([SESSIONS_STORE, PARTS_STORE], "readwrite");

    await new Promise<void>((resolve, reject) => {
      const sessionDelete = tx.objectStore(SESSIONS_STORE).delete(sessionId);
      sessionDelete.onerror = () => reject(sessionDelete.error);

      const partsIndex = tx.objectStore(PARTS_STORE).index("sessionId");
      const partsRequest = partsIndex.getAll(sessionId);
      partsRequest.onsuccess = () => {
        const parts = partsRequest.result || [];
        parts.forEach((part) => {
          tx.objectStore(PARTS_STORE).delete(part.id);
        });
      };
      partsRequest.onerror = () => reject(partsRequest.error);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}

export default new RecordingSessionService();
